/**
 * What a refused AI test call says: the test-call route's error codes and the
 * reader that turns a thrown error into a key the button and dialog can print.
 *
 * Its own file rather than more of `aiOutbound.ts`: the code list grows with
 * every new refusal the route learns, while the settings mirror beside it does
 * not, and that file was already at the size limit. `aiOutbound.ts` re-exports
 * everything here, so existing imports keep working.
 *
 * A HAND-WRITTEN MIRROR of `TEST_CALL_ERRORS` in
 * `apps/api/src/modules/crm-calls/lib/outbound/test-call.ts`. Every code needs
 * a key under `aiOutbound.testCall.error` (`aiOutboundNotes.test.ts` holds the
 * catalogues to that).
 *
 * React-free on purpose: `aiTestCallErrors.test.ts` runs it under plain node.
 */
import { ApiError } from "./api";

/** Every refusal the test-call route names. */
export const TEST_CALL_ERRORS = [
  "invalid_body",
  "invalid_domain",
  "unknown_company",
  "unknown_campaign",
  "no_domain",
  "opening_invalid",
  "flow_invalid",
  "bad_number",
  "disabled",
  "not_ready",
  "no_test_numbers",
  "busy",
  "own_line",
  "suppressed",
  "test_daily_cap",
  "not_configured",
  "db_failed",
  "twilio_failed",
  // Alert calls (ring R9): the first comes from the route (no callable defect), the other five from the pre-dial step.
  "no_alert",
  "defect_gone",
  "alert_unchecked",
  "alert_too_old",
  "alert_called",
  "site_excluded",
] as const;

/** The refusals that mean the cached status the button was drawn from is out of date. */
const STALE_STATUS_ERRORS: ReadonlySet<string> = new Set(["disabled", "not_ready", "no_test_numbers"]);
const KNOWN_ERRORS: ReadonlySet<string> = new Set<string>(TEST_CALL_ERRORS);

export interface TestCallError {
  /** Suffix under `aiOutbound.testCall.error`: a known code, "forbidden" or "failed". */
  key: string;
  /** The day's test-call ceiling from a 429, when the server named it. */
  cap: number | null;
  /** The refused test number from a 409 `suppressed`. */
  number: string | null;
  /** The button should fetch the status again before it is pressed again. */
  refreshStatus: boolean;
}

/** A thrown error as something the button can say. */
export function testCallError(err: unknown): TestCallError {
  const out: TestCallError = { key: "failed", cap: null, number: null, refreshStatus: false };
  if (!(err instanceof ApiError)) return out;
  if (err.status === 403) return { ...out, key: "forbidden" };
  const body = (err.body ?? {}) as { error?: unknown; cap?: unknown; number?: unknown };
  const code = typeof body.error === "string" ? body.error : "";
  if (!KNOWN_ERRORS.has(code)) return out;
  const { cap, number } = body;
  return {
    key: code,
    cap: typeof cap === "number" ? cap : null,
    number: typeof number === "string" && number ? number : null,
    refreshStatus: STALE_STATUS_ERRORS.has(code),
  };
}
